// filepath: apps/web/src/admin/counterparties/new.tsx
//
// FR-Group-B — CounterpartyNewScreen (ISS-442 i18n).
//
// Create-counterparty form. Mirrors the legal_entity / organization_unit
// create patterns under apps/web/src/admin/* (when present) but stays
// self-contained — the counterparty form has a smaller field surface
// than the full directory.org surface and shares no schema.
//
// ISS-442 BA spec (verbatim):
//   (b) Translation all locales (RU authoritative, KK falls back
//       to RU when a key is missing, EN must have real English).
//       No hardcoded strings. i18n namespace
//       `directory.counterparties.*`.
//
// ISS-431-uatrzt2 React crash fix: the project opts out of
// `react-router-dom` (FR-UI-CABINET-1 AC-9) and uses its own
// `navigate()`. The previous `useNavigate` import threw on every
// render.
//
// INV-8: submit always carries a fresh X-Idempotency-Key generated
// via `newIdempotencyKey()` so the SSR-side replay guard returns
// 200 with `{"status":"already processed"}` on double-submit rather
// than producing a duplicate row.
//
// INV-9: all values render as JSX text — no `dangerouslySetInnerHTML`.

import React, { useCallback, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Card } from "@/design-system/Card";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { navigate } from "@/features/cabinet/usePathname";
import { ApiError } from "@/definitions/types";
import {
  CreateCounterpartyInput,
  LegalForm,
  LEGAL_FORMS,
  createCounterparty,
  newIdempotencyKey,
} from "./api";

/**
 * ISS-448 acceptance checks AC-1 (Create) + AC-3 (validation)
 * + AC-4 (unprivileged) + AC-5 (edge cases).
 *
 * ISS-448 round 2 (B2, architect note_13946): the classifiers
 * check the TYPED `ApiError` fields (`.status` + `.code`),
 * not string substrings. The pre-round-2 string-substring
 * classifier matched the (mocked) error message but the
 * real `callApi` throw path produced bare "HTTP 403" /
 * "HTTP 409" messages — substring matching was unreliable.
 * The typed check is robust against any server-side change
 * to the error message body.
 */
function isPermissionDenied(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof ApiError) {
    return e.status === 403 || e.status === 404;
  }
  return false;
}

function isTaxIdConflict(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof ApiError) {
    return e.status === 409 && e.code === "tax_id_conflict";
  }
  return false;
}

/**
 * ISS-431-uatrzt2 React crash fix: see detail.tsx for the
 * rationale. The previous `useNavigate` import from
 * `react-router-dom` threw on every render. Replaced with the
 * project's own `navigate()` (pushState + synthetic popstate).
 */

export interface CounterpartyNewScreenProps {
  /** Test override for the submit pipeline. */
  submit?: (input: CreateCounterpartyInput, key: string) => Promise<void>;
}

export function CounterpartyNewScreen({
  submit,
}: CounterpartyNewScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();

  const [name, setName] = useState<string>("");
  const [taxId, setTaxId] = useState<string>("");
  // ISS-459 BA correction: the default legal_form is "other".
  // The closed enum is {sole_prop, llc, jsc, plc, other}. The
  // pre-ISS-448 default "corporation" was rejected (not in the
  // enum), the ISS-448 default "llc" was wrong because llc
  // requires a tax_id (migration 0093) but tax_id is optional
  // in the form. "other" has no tax_id requirement, so an
  // untouched form is valid (server-side: legal_form=other
  // + tax_id=null passes migration 0093's NOT NULL + the
  // legal_form CHECK).
  const [legalForm, setLegalForm] = useState<LegalForm>("other");
  // ISS-448 BA spec: contact_person_id is OUT of v1 (needs a
  // person picker; the BA deferred the person picker to a
  // separate FR). The form no longer surfaces a contact_person_id
  // input.
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // ISS-448 AC-4: the create path renders <PermissionDenied />
  // when the caller lacks directory.access (the server
  // returns 403, which api.ts callApi prefixes with http_403).
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  // ISS-448 AC-3: duplicate tax_id is rendered as a FIELD
  // error on the tax_id input, not as a generic form-level
  // banner. The server returns 409 with code=tax_id_conflict
  // (per internal/directory/counterparty_handlers.go:432).
  const [taxIdError, setTaxIdError] = useState<string | null>(null);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);
      setPermissionDenied(false);
      setTaxIdError(null);
      const input: CreateCounterpartyInput = {
        name: name.trim(),
        legal_form: legalForm,
      };
      if (taxId.trim()) input.tax_id = taxId.trim();
      if (email.trim()) input.email = email.trim();
      if (phone.trim()) input.phone = phone.trim();
      if (address.trim()) input.address = address.trim();
      if (notes.trim()) input.notes = notes.trim();

      const key = newIdempotencyKey();
      try {
        if (submit) {
          await submit(input, key);
        } else {
          await createCounterparty(input, key);
        }
        navigate("/admin/counterparties");
      } catch (e2) {
        // ISS-448 AC-3: route the 3 error classes to the right
        // surface. (a) 403 / 404 -> <PermissionDenied />
        // (unprivileged). (b) 409 tax_id_conflict -> field-
        // level tax_id error. (c) anything else -> generic
        // form-level banner. (d) Cyrillic text is rendered
        // unchanged by the form (the <input> + <textarea>
        // accept full Unicode; the JSX text nodes are
        // untouched by the API).
        const msg = e2 instanceof Error ? e2.message : String(e2);
        if (isPermissionDenied(e2)) {
          setPermissionDenied(true);
        } else if (isTaxIdConflict(e2)) {
          setTaxIdError(
            t(
              "directory.counterparties.new.tax_id_conflict",
              "A counterparty with this tax id already exists in this tenant",
            ),
          );
        } else {
          setError(msg);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [name, taxId, legalForm, email, phone, address, notes, submit],
  );

  return (
    <section data-testid="counterparty-new-screen">
      <header>
        <h1 id="counterparty-new-title">
          {t("directory.counterparties.new.title", "New counterparty")}
        </h1>
      </header>
      {permissionDenied ? (
        // ISS-448 AC-4: unprivileged viewer sees the shared
        // <PermissionDenied /> page (the same component the
        // list screen uses — see list.tsx:199). The
        // component itself is role="alert" so screen readers
        // announce it.
        <div data-testid="counterparty-new-permission-denied">
          <PermissionDenied />
        </div>
      ) : error ? (
        <Banner tone="danger" data-testid="counterparty-new-error">
          {t("directory.counterparties.new.error", "Error: {{message}}", {
            message: error,
          })}
        </Banner>
      ) : null}
      <Card>
        <form onSubmit={onSubmit} aria-labelledby="counterparty-new-title">
          <div>
            <label htmlFor="counterparty-new-name">
              {t("directory.counterparties.detail.field.name", "Name")}
            </label>
            <input
              id="counterparty-new-name"
              required
              minLength={1}
              // ISS-448 BA spec: name up to 512 chars.
              maxLength={512}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="counterparty-new-name"
            />
          </div>
          {/* ISS-442 (a): tax_id + legal_form now in the new form.
              ISS-448 BA spec: tax_id up to 64 chars + unique per
              tenant. Field-level error on 409 tax_id_conflict. */}
          <div>
            <label htmlFor="counterparty-new-tax-id">
              {t(
                "directory.counterparties.detail.field.tax_id (optional)",
                "Tax id (optional)",
              )}
            </label>
            <input
              id="counterparty-new-tax-id"
              maxLength={64}
              value={taxId}
              onChange={(e) => {
                setTaxId(e.target.value);
                if (taxIdError) setTaxIdError(null);
              }}
              aria-invalid={taxIdError ? true : undefined}
              aria-describedby={
                taxIdError ? "counterparty-new-tax-id-error" : undefined
              }
              data-testid="counterparty-new-tax-id"
            />
            {taxIdError ? (
              <p
                role="alert"
                id="counterparty-new-tax-id-error"
                data-testid="counterparty-new-tax-id-error"
                className="counterparty-new-field-error"
              >
                {taxIdError}
              </p>
            ) : null}
          </div>
          <div>
            <label htmlFor="counterparty-new-legal-form">
              {t(
                "directory.counterparties.detail.field.legal_form",
                "Legal form",
              )}
            </label>
            <select
              id="counterparty-new-legal-form"
              required
              value={legalForm}
              onChange={(e) => setLegalForm(e.target.value as LegalForm)}
              data-testid="counterparty-new-legal-form"
            >
              {LEGAL_FORMS.map((lf) => (
                <option key={lf} value={lf}>
                  {t(`directory.counterparties.legal_form.${lf}`, lf)}
                </option>
              ))}
            </select>
          </div>
          {/* ISS-448 BA spec: contact_person_id is OUT of v1
              (needs a person picker; BA deferred to a separate
              FR). The field is removed from the form. */}
          <div>
            <label htmlFor="counterparty-new-email">
              {t(
                "directory.counterparties.detail.field.email (optional)",
                "Email (optional)",
              )}
            </label>
            <input
              id="counterparty-new-email"
              type="email"
              // ISS-448 BA spec: email up to 320 chars.
              maxLength={320}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="counterparty-new-email"
            />
          </div>
          <div>
            <label htmlFor="counterparty-new-phone">
              {t(
                "directory.counterparties.detail.field.phone (optional)",
                "Phone (optional)",
              )}
            </label>
            <input
              id="counterparty-new-phone"
              type="tel"
              // ISS-448 BA spec: phone up to 64 chars.
              maxLength={64}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="counterparty-new-phone"
            />
          </div>
          <div>
            <label htmlFor="counterparty-new-address">
              {t(
                "directory.counterparties.detail.field.address (optional)",
                "Address (optional)",
              )}
            </label>
            <input
              id="counterparty-new-address"
              type="text"
              maxLength={500}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="counterparty-new-address"
            />
          </div>
          <div>
            <label htmlFor="counterparty-new-notes">
              {t(
                "directory.counterparties.detail.field.notes (optional)",
                "Notes (optional)",
              )}
            </label>
            <textarea
              id="counterparty-new-notes"
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="counterparty-new-notes"
            />
          </div>
          <div className="counterparty-new-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || name.trim().length === 0}
              data-testid="counterparty-new-submit"
            >
              {submitting
                ? t("directory.counterparties.new.submitting", "Creating…")
                : t(
                    "directory.counterparties.new.submit",
                    "Create counterparty",
                  )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/admin/counterparties")}
              data-testid="counterparty-new-cancel"
            >
              {t("directory.counterparties.new.cancel", "Cancel")}
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}

export default CounterpartyNewScreen;
